import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Lock, Mail, User, Building2 } from "lucide-react";
import { API } from "../App";
import { toast } from "sonner";
import { formatErrorMessage } from "../utils/errorHandler";
import { SECTOR_MODULES } from "../config/sectorModules";
import { setFavicon, setPageTitle } from "../utils/favicon";

const AuthPage = ({ onLogin }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    business_type: "mobile_shop",
  });
  
  // Load cached branding immediately to avoid flash of default content
  const getCachedBranding = () => {
    try {
      const cached = localStorage.getItem('cached_branding');
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  };
  
  const cachedBranding = getCachedBranding();
  const [logoUrl, setLogoUrl] = useState(cachedBranding?.logo_url || null);
  const [websiteName, setWebsiteName] = useState(cachedBranding?.website_name || null);
  const [backgroundImageUrl, setBackgroundImageUrl] = useState(cachedBranding?.background_image_url || null);
  const [brandingLoaded, setBrandingLoaded] = useState(!!cachedBranding);

  useEffect(() => {
    // Apply cached branding immediately
    if (cachedBranding) {
      if (cachedBranding.favicon_url) setFavicon(cachedBranding.favicon_url);
      if (cachedBranding.website_name) setPageTitle(cachedBranding.website_name);
    }
    
    const fetchBranding = async () => {
      try {
        // Try to get tenant_slug from URL or localStorage
        const urlParams = new URLSearchParams(window.location.search);
        const tenantSlug = urlParams.get('tenant') || localStorage.getItem('last_tenant_slug');
        
        const url = tenantSlug 
          ? `${API}/public/branding?tenant_slug=${tenantSlug}`
          : `${API}/public/branding`;
        
        const response = await axios.get(url);
        if (response.data) {
          setLogoUrl(response.data.logo_url || null);
          setWebsiteName(response.data.website_name || null);
          setBackgroundImageUrl(response.data.background_image_url || null);
          setFavicon(response.data.favicon_url);
          if (response.data.website_name) {
            setPageTitle(response.data.website_name);
          }
          // Cache branding for instant load next time
          localStorage.setItem('cached_branding', JSON.stringify(response.data));
        }
      } catch (error) {
        console.log("Using default branding");
      } finally {
        setBrandingLoaded(true);
      }
    };

    fetchBranding();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? "/auth/login" : "/auth/signup";
      const payload = isLogin
        ? { email: formData.email, password: formData.password }
        : {
            email: formData.email,
            admin_password: formData.password,
            name: formData.name,
            business_type: formData.business_type,
          };
      const response = await axios.post(`${API}${endpoint}`, payload);

      onLogin(response.data.user, response.data.access_token);
      toast.success(
        isLogin ? "Welcome back!" : "Account created successfully!",
      );
    } catch (error) {
      toast.error(formatErrorMessage(error, "Authentication failed"));
    } finally {
      setLoading(false);
    }
  };

  const containerStyle = backgroundImageUrl
    ? {
        backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.85), rgba(15, 23, 42, 0.85)), url(${backgroundImageUrl})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }
    : {};

  // Use custom-bg class when we have a custom background to avoid the default tech-bg overlay
  const bgClass = backgroundImageUrl ? "custom-bg" : "gradient-bg";

  // Show loading spinner while fetching branding (only if no cached data)
  if (!brandingLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-400"></div>
          <p className="text-slate-400 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen flex items-center justify-center p-4 ${bgClass} relative`}
      style={containerStyle}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative z-10"
      >
        <div className="glass-card p-8">
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200 }}
              className="inline-flex items-center justify-center mb-4"
            >
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt="Logo"
                  className="w-16 h-16 rounded-xl object-cover"
                />
              ) : (
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-700 rounded-2xl flex items-center justify-center">
                  <Building2 className="w-8 h-8 text-white" />
                </div>
              )}
            </motion.div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">
              {websiteName || "Smart Business ERP"}
            </h1>
            <p className="text-slate-400 mt-2">
              {isLogin ? "Welcome back" : "Create your account"}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Business Name
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      data-testid="auth-businessname-input"
                      type="text"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      className="w-full pl-11 pr-4 py-3 rounded-xl bg-slate-800/50 border border-slate-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 text-white placeholder-slate-500"
                      placeholder="Enter your business name"
                      required={!isLogin}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Business Type
                  </label>
                  <div className="relative">
                    <select
                      data-testid="auth-businesstype-select"
                      value={formData.business_type}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          business_type: e.target.value,
                        })
                      }
                      className="w-full px-4 py-3 rounded-xl bg-slate-800/50 border border-slate-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 text-white"
                      required={!isLogin}
                    >
                      {Object.entries(SECTOR_MODULES).map(([key, config]) => (
                        <option key={key} value={key}>
                          {config.name} {config.icon}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  data-testid="auth-email-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className="w-full pl-11 pr-4 py-3 rounded-xl"
                  placeholder="Enter your email"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  data-testid="auth-password-input"
                  type="password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  className="w-full pl-11 pr-4 py-3 rounded-xl"
                  placeholder="Enter your password"
                  required
                />
              </div>
            </div>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="auth-submit-button"
              type="submit"
              disabled={loading}
              className="w-full btn-primary py-3 text-lg font-semibold"
            >
              {loading
                ? "Processing..."
                : isLogin
                  ? "Sign In"
                  : "Create Account"}
            </motion.button>
          </form>

        </div>
      </motion.div>
    </div>
  );
};

export default AuthPage;
